import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type DragEvent, type ReactNode } from 'react';
import { api, ApiError } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatDayText } from '../events/fields';
import type { EventView } from '../events/labels';
import { PEOPLE_KEY, PERSONS_RESOURCE, type Person } from '../people/types';
import { dropOn } from '../quotes/lineOrder';
import { DialogLayer } from '../ui/Dialog';
import { RowMenu } from '../ui/RowMenu';
import { ConflictNotice } from '../ui/ConflictNotice';
import { ASSIGNMENT_STATUSES, CREW_KEY, CREW_RESOURCE, dayShort, hoursText, type Assignment, type AssignmentStatus } from './types';
import { blockedDays, unavailableLabel, useUnavailable, type UnavailableDays } from './unavailable';
import { ObsadaInfo } from './ObsadaInfo';
import { refreshCrew } from './refreshCrew';
import { todayInWarsaw } from '../events/calendar';
import { isFilled, mayTake, otherDaysText, releasedText, type CountResult, placeName, placeWho, type ChangeResult, type FillResult, type OtherDaysResult, type Place, type PlaceGroup, type PositionsView } from './positions';
import { Notice } from '../ui/Notice';
import { selfMarkAsk } from '../availability/peopleGrid';
import { useSelfMarkAsk } from '../availability/SelfMarkDialog';

function errorText(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback;
  const details = Array.isArray(error.details) ? (error.details as { message?: unknown }[]).map((d) => d.message).filter((m): m is string => typeof m === 'string') : [];
  return details.length > 0 ? details.join('. ') : error.message;
}

/** The collisions after a fill or a change: „Kolizja 7.11: także „Koncert B””. */
const collisionsText = (list: OtherDaysResult['collisions']) => list.map((c) => `Kolizja ${dayShort(c.day)}: także „${c.events.map((e) => e.eventName).join('”, „')}”.`).join(' ');

type Row = { place: Place; extra: boolean };

/**
 * Obsada — new layout (owner 2026-10-26 07:36–07:40; it replaces the add row, „+ Stanowisko”, „×” and the summary of
 * #193/#198). On top „ⓘ Info” with the numbers per function (and ⚙, the global setting); below ONLY the list of places,
 * in the global order of the functions: the function, the person picker (or the person, with „Zmień”) and the status.
 * Kept on the places: only people with the function („Inni”: anybody), unavailable people greyed out, a refusal said in
 * its row, collisions with „Wymuś”, „Obsadzić tę osobę także w pozostałych dniach?”, free places after filled ones,
 * drag order, „Wyślij zapytanie” beside a free place of the rules. People of the crew in no place (beyond the number,
 * „Odmowa”) are listed under their function („poza liczbą”). After every change the quote's green bar asks (`onChanged`).
 */
export function PositionsPanel({
  event,
  assignments = [],
  onAsk,
  onChanged,
  onRemove,
  badge,
  infoFilled = false,
}: {
  event: EventView;
  /** Owner 2026-10-27 08:16: the grid's side panel — ⓘ Info says the filled places per function (they left the bar). */
  infoFilled?: boolean;
  assignments?: Assignment[];
  onAsk?: (functionId: string) => void;
  onChanged?: () => void;
  /** „Usuń z obsady” of a person outside the places (the confirmation dialog of the tab). */
  onRemove?: (a: Assignment) => void;
  /** The answers to availability requests beside a person (F20). */
  badge?: (personId: string) => ReactNode;
}) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const key = [...CREW_KEY, 'positions', event.id];
  const view = useQuery({ queryKey: key, queryFn: () => api<PositionsView>(`/api/crew/events/${event.id}/positions`) });
  const mayEdit = !!me && can(me, CREW_RESOURCE, 'edit') && fieldVisible(me, CREW_RESOURCE, 'functionId');
  const mayStatus = !!me && can(me, CREW_RESOURCE, 'edit') && fieldVisible(me, CREW_RESOURCE, 'status');
  const mayFill = !!me && can(me, CREW_RESOURCE, 'create') && fieldVisible(me, CREW_RESOURCE, 'personId') && fieldVisible(me, CREW_RESOURCE, 'functionId') && can(me, PERSONS_RESOURCE, 'view');
  const mayChange = mayFill && !!me && can(me, CREW_RESOURCE, 'delete');
  // #199 (owner 2026-10-26 07:42): take a person off a place — the place stays, free (a gap on the grid's events strip)
  const mayRelease = !!me && can(me, CREW_RESOURCE, 'delete') && fieldVisible(me, CREW_RESOURCE, 'personId');
  const persons = useQuery({ queryKey: [...PEOPLE_KEY, 'persons', 'crew-candidates', ''], enabled: mayFill, queryFn: () => api<{ persons: Person[]; truncated?: boolean }>('/api/people/persons') });

  const [notice, setNotice] = useState<string | null>(null);
  const [askDays, setAskDays] = useState<{ positionId: string; personName: string; days: string[] } | null>(null);
  const [changing, setChanging] = useState<string | null>(null);
  const [lastDay, setLastDay] = useState<{ assignmentId: string; day: string; personName: string } | null>(null);
  // production 2026-10-26: a refused pick says so in its own row, and the row's picker is back on „wolne”
  const [rowError, setRowError] = useState<{ key: string; text: string } | null>(null);
  const unavailable = useUnavailable(event.id, mayFill);
  const [drag, setDrag] = useState<{ groupKey: string; id: string } | null>(null);
  const byId = new Map(assignments.map((a) => [a.id, a]));
  // every crew change: Obsada, the grid, its events strip and side panel read again (owner 2026-10-26 07:42)
  const refresh = () => refreshCrew(queryClient);
  /** Owner 18:07 / 22:02: after every change the green bar asks about the quote. */
  const changed = async () => {
    await refresh();
    onChanged?.();
  };
  /** After a fill: the other days to ask about, else the quote. */
  const afterFill = async (r: { positionId?: string; otherDays: string[]; collisions?: OtherDaysResult['collisions'] }, personName: string) => {
    setNotice(r.collisions && r.collisions.length > 0 ? collisionsText(r.collisions) : null);
    await refresh();
    if (r.positionId && r.otherDays.length > 0) setAskDays({ positionId: r.positionId, personName, days: r.otherDays });
    else onChanged?.();
  };
  const refused = (k: string) => (error: unknown) => setRowError({ key: k, text: errorText(error, 'Nie udało się obsadzić') });
  const fill = useMutation({
    mutationFn: ({ place, personId }: { place: Place; personId: string; personName: string; key: string }) => api<FillResult>(`/api/crew/positions/${place.id}/fill`, { method: 'POST', body: { personId } }),
    onMutate: () => setRowError(null),
    onSuccess: (r, v) => afterFill(r, v.personName),
    onError: (e, v) => refused(v.key)(e),
  });
  // a free place of the rules: the person joins as with „Dodaj osobę” (the whole event)
  const fillRule = useMutation({
    mutationFn: (b: { personId: string; functionId: string; personName: string; key: string }) => api(`/api/crew/events/${event.id}/assignments`, { method: 'POST', body: { personId: b.personId, functionId: b.functionId } }),
    onMutate: () => setRowError(null),
    onSuccess: changed,
    onError: (e, v) => refused(v.key)(e),
  });
  // owner 21:53: „Zmień osobę” — a place with a row, or the assignment behind a place of the rules / outside the places
  const change = useMutation({
    mutationFn: (b: { place: Place; personId: string; personName: string; key: string }) =>
      b.place.id
        ? api<ChangeResult>(`/api/crew/positions/${b.place.id}/change`, { method: 'POST', body: { personId: b.personId } })
        : api<ChangeResult>(`/api/crew/assignments/${b.place.assignmentId}/change-person`, { method: 'POST', body: { personId: b.personId, version: byId.get(b.place.assignmentId ?? '')?.version ?? 0 } }),
    onMutate: () => setRowError(null),
    onSuccess: async (r, b) => {
      setChanging(null);
      await afterFill(r, b.personName);
    },
    onError: (e, v) => refused(v.key)(e),
  });
  const releasePerson = useMutation({
    mutationFn: (b: { assignmentId: string; day: string }) => api<CountResult>(`/api/crew/assignments/${b.assignmentId}/release`, { method: 'POST', body: { day: b.day } }),
    onSuccess: async (r) => {
      setNotice(releasedText(r.released));
      await changed();
    },
  });
  const fillOthers = useMutation({
    mutationFn: (a: { positionId: string }) => api<OtherDaysResult>(`/api/crew/positions/${a.positionId}/fill-other-days`, { method: 'POST', body: {} }),
    onSuccess: async (r) => {
      setAskDays(null);
      setNotice(otherDaysText(r));
      await changed();
    },
  });
  const describe = useMutation({
    mutationFn: ({ place, description }: { place: Place; description: string }) => api(`/api/crew/positions/${place.id}`, { method: 'PATCH', body: { description, version: place.version } }),
    onSettled: refresh,
  });
  const order = useMutation({
    mutationFn: (b: { day: string; functionId: string | null; positions: Array<{ id: string; version: number }> }) => api(`/api/crew/events/${event.id}/positions/order`, { method: 'PUT', body: b }),
    onSettled: refresh,
  });
  // review I2: „Odmowa” over the person's own mark („Mój kalendarz”) — „Wpis pracownika” first, then with the token
  const selfAsk = useSelfMarkAsk();
  const changeStatus = useMutation({
    mutationFn: ({ a, status, consulted }: { a: Assignment; status: AssignmentStatus; consulted?: string }) =>
      api<Assignment>(`/api/crew/assignments/${a.id}`, { method: 'PATCH', body: { version: a.version, status, ...(consulted ? { consulted } : {}) } }),
    onSuccess: changed,
    onError: (e, v) => {
      selfAsk.offer(e, (consulted) => changeStatus.mutate({ ...v, consulted }));
    },
  });
  // „Dostępność → Ludzie” 1b D: „Wymuś” — the person works here that day, inactive in the other event
  const force = useMutation({
    mutationFn: ({ a, day }: { a: Assignment; day: string }) => api(`/api/crew/assignments/${a.id}/force`, { method: 'POST', body: { days: [day] } }),
    onSuccess: changed,
  });
  if (!me || !view.data) return null;
  const { days, multiDay } = view.data;
  // a `self_mark` is the question „Wpis pracownika”, not an error
  const error = [releasePerson, fillOthers, describe, order, changeStatus, force].map((m) => m.error).find((e) => e && !selfMarkAsk(e));
  const conflict = error instanceof ApiError && error.status === 409;
  const people = persons.data?.persons ?? [];
  const busy = [fill, fillRule, change, releasePerson].some((m) => m.isPending);
  /** The days the view shows an assignment on (in a place or outside the places). */
  const presentDays = (assignmentId: string) => days.filter((d) => d.groups.some((g) => [...g.places, ...(g.extra ?? [])].some((x) => x.assignmentId === assignmentId))).map((d) => d.day);
  const shownDays = days.filter((d) => d.groups.some((g) => g.places.length > 0 || (g.extra ?? []).length > 0));

  const dropTarget = (d: string, g: PlaceGroup, p: Place) =>
    mayEdit && g.source === 'manual' && p.id
      ? {
          onDragOver: (e: DragEvent<HTMLLIElement>) => {
            if (drag?.groupKey !== `${d}:${g.functionId}`) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
          },
          onDrop: (e: DragEvent<HTMLLIElement>) => {
            if (!drag || drag.groupKey !== `${d}:${g.functionId}`) return;
            e.preventDefault();
            const box = e.currentTarget.getBoundingClientRect();
            const next = dropOn(g.places.map((x) => x.id!), drag.id, p.id!, e.clientY > box.top + box.height / 2);
            setDrag(null);
            if (!next) return;
            const rows = new Map(g.places.map((x) => [x.id!, x]));
            order.mutate({ day: d, functionId: g.others ? null : g.functionId, positions: next.map((id) => ({ id, version: rows.get(id)!.version! })) });
          },
        }
      : {};

  const personSelect = (g: PlaceGroup, day: string, label: string, onPick: (p: Person) => void, emptyText = 'wolne — wybierz osobę', except?: string) => (
    <select
      className="crew-position-fill"
      aria-label={label}
      value=""
      disabled={busy}
      onChange={(e) => {
        const person = people.find((x) => x.id === e.target.value);
        if (person) onPick(person);
      }}
    >
      <option value="">{emptyText}</option>
      {personOptions(people.filter((x) => mayTake(x, g) && x.id !== except), unavailable, [day])}
    </select>
  );

  /** The status of the person in a place: a select for whoever may change it, else the text. */
  const statusOf = (a: Assignment | undefined) =>
    a?.status ? (
      mayStatus ? (
        <select className="crew-place-status" aria-label={`Status: ${a.personName ?? 'osoba'}${a.functionName ? ` (${a.functionName})` : ''}`} value={a.status} disabled={changeStatus.isPending} onChange={(e) => changeStatus.mutate({ a, status: e.target.value as AssignmentStatus })}>
          {(Object.keys(ASSIGNMENT_STATUSES) as AssignmentStatus[]).map((st) => (
            <option key={st} value={st}>
              {ASSIGNMENT_STATUSES[st]}
            </option>
          ))}
        </select>
      ) : (
        <span className="crew-status" data-status={a.status}>
          {ASSIGNMENT_STATUSES[a.status]}
        </span>
      )
    ) : null;

  /** 1b: the day's collision, unavailability or inactivity of the person in this place — with „Wymuś”. */
  const dayNotes = (a: Assignment | undefined, day: string) => {
    const d = a?.days?.find((x) => x.day === day);
    if (!a || !d) return null;
    if (d.unavailable && !d.collision) return <span className="crew-collision">Niedostępny {dayShort(day)} — a jest w obsadzie</span>;
    if (d.collision) {
      return (
        <span className="crew-collision" data-feature-id="crew.people_availability">
          Kolizja {dayShort(day)}: także „{d.collision.map((c) => c.eventName).join('”, „')}”{d.unavailable ? ' — i niedostępny' : ''}
          {mayStatus && !d.unavailable && (
            <button className="btn ghost small" type="button" disabled={force.isPending} aria-label={`Wymuś ${dayShort(day)}: ${a.personName ?? 'osoba'}`} onClick={() => force.mutate({ a, day })}>
              Wymuś
            </button>
          )}
        </span>
      );
    }
    if (!d.active) {
      return (
        <span className="crew-inactive">
          Nieaktywny {dayShort(day)}
          {d.because ? ` — robi „${d.because.eventName}”` : ''}
          {mayStatus && !d.past && (
            <button className="btn ghost small" type="button" disabled={force.isPending} aria-label={`Wymuś ${dayShort(day)}: ${a.personName ?? 'osoba'}`} onClick={() => force.mutate({ a, day })}>
              Wymuś
            </button>
          )}
        </span>
      );
    }
    return null;
  };

  const placeRow = (d: string, g: PlaceGroup, { place: p, extra }: Row) => {
    const label = extra ? `${g.functionName} — poza liczbą (${placeWho(p)})` : p.label;
    const name = placeName(d, label, multiDay);
    const filled = isFilled(p);
    const rowKey = `${d}:${g.functionId}:${extra ? `x:${p.assignmentId}` : (p.id ?? p.place)}`;
    const a = p.assignmentId ? byId.get(p.assignmentId) : undefined;
    const canChange = filled && mayChange && !!p.personId && (!!p.id || !!p.assignmentId);
    return (
      <li key={rowKey} className={`crew-place${filled ? ' full' : ' open'}${extra ? ' extra' : ''}${a?.status === 'odmowa' ? ' declined' : ''}${drag?.id === p.id ? ' dragging' : ''}`} aria-label={name} {...(extra ? {} : dropTarget(d, g, p))}>
        {!extra && mayEdit && g.source === 'manual' && p.id ? (
          <span
            className="crew-position-grip"
            draggable
            aria-hidden="true"
            title="Przeciągnij, żeby zmienić kolejność"
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/plain', p.id!);
              setDrag({ groupKey: `${d}:${g.functionId}`, id: p.id! });
            }}
            onDragEnd={() => setDrag(null)}
          />
        ) : (
          <span className="crew-position-grip-space" aria-hidden="true" />
        )}
        <span className="crew-place-fn">{g.functionName}</span>
        {g.others && p.id && (
          <input
            className="crew-position-desc"
            aria-label={`Opis: ${name}`}
            placeholder="Opis (np. kierowca)"
            defaultValue={p.description ?? ''}
            maxLength={200}
            readOnly={!mayEdit}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (mayEdit && v !== (p.description ?? '')) describe.mutate({ place: p, description: v });
            }}
          />
        )}
        <span className="crew-place-who">
          {filled ? (
            changing === rowKey ? (
              personSelect(g, d, `Zmień osobę: ${name}`, (x) => change.mutate({ place: p, personId: x.id, personName: x.name ?? 'Osoba', key: rowKey }), `zamiast: ${placeWho(p)}`, p.personId)
            ) : (
              <>
                <span className="crew-place-person">{placeWho(p)}</span>
                {p.personId && badge?.(p.personId)}
                {a?.availabilityConfirmed && (
                  <span className="crew-confirmed" data-feature-id="requests.dates">
                    dostępność potwierdzona
                  </span>
                )}
                {canChange && (
                  <button className="btn ghost small" type="button" aria-label={`Zmień osobę: ${name}`} onClick={() => setChanging(rowKey)}>
                    Zmień
                  </button>
                )}
                {!extra && mayRelease && !!p.assignmentId && !!p.personId && d >= todayInWarsaw() && (
                  <button
                    className="btn ghost small"
                    type="button"
                    aria-label={`Usuń osobę ze stanowiska: ${name}`}
                    title="Zdejmij osobę z miejsca — miejsce zostaje wolne (do obsadzenia)"
                    disabled={busy}
                    onClick={() => {
                      // review I1: their last day here — the release takes them out of the crew: ask first
                      if (presentDays(p.assignmentId!).every((x) => x === d)) setLastDay({ assignmentId: p.assignmentId!, day: d, personName: placeWho(p) });
                      else releasePerson.mutate({ assignmentId: p.assignmentId!, day: d });
                    }}
                  >
                    Zdejmij
                  </button>
                )}
              </>
            )
          ) : mayFill && (p.id || g.source === 'rule') ? (
            personSelect(g, d, `Obsadź: ${name}`, (x) =>
              p.id
                ? fill.mutate({ place: p, personId: x.id, personName: x.name ?? 'Osoba', key: rowKey })
                : fillRule.mutate({ personId: x.id, functionId: g.functionId, personName: x.name ?? 'Osoba', key: rowKey }),
            )
          ) : (
            <span className="crew-place-person">{placeWho(p)}</span>
          )}
        </span>
        {a?.startsAt && <span className="crew-position-source">{hoursText(a.startsAt, a.endsAt)}</span>}
        {extra && <span className="crew-position-source">poza liczbą</span>}
        {filled && statusOf(a)}
        {!filled && g.source === 'rule' && onAsk && (
          <button className="btn ghost small crew-demand-ask" type="button" data-feature-id="requests.send" aria-label={`Wyślij zapytanie: ${p.label}`} onClick={() => onAsk(g.functionId)}>
            Wyślij zapytanie
          </button>
        )}
        {/* review I1: „Usuń z obsady” (the tab's confirmation) for every person in the crew; C1: not for a person the role may not see */}
        {a && p.personId && onRemove && can(me, CREW_RESOURCE, 'delete') && (
          <RowMenu label={`Więcej działań: ${a.personName ?? 'osoba'} (${g.functionName})`} actions={[{ label: 'Usuń z obsady', danger: true, onSelect: () => onRemove(a) }]} />
        )}
        {(filled || a) && dayNotes(a, d) && <span className="crew-place-notes">{dayNotes(a, d)}</span>}
        {a?.datesChanged && <span className="crew-reask">zapytaj ponownie (zmieniona data)</span>}
        {rowError?.key === rowKey && (
          <span className="crew-row-error" role="alert">
            {rowError.text}
          </span>
        )}
      </li>
    );
  };

  return (
    <section className="crew-positions" aria-label="Stanowiska" data-feature-id="crew.positions">
      <ObsadaInfo eventId={event.id} {...(onChanged ? { onChanged } : {})} {...(infoFilled ? { filled: view.data, variant: 'panel' as const } : {})} />
      {notice && (
        <Notice className="crew-positions-notice" onClose={() => setNotice(null)}>
          {notice}
        </Notice>
      )}
      {conflict && <ConflictNotice onReload={() => void refresh()} />}
      {selfAsk.dialog}
      {error && !conflict && (
        <p className="alert" role="alert">
          {errorText(error, 'Nie udało się zmienić obsady')}
        </p>
      )}
      {shownDays.length === 0 && <p className="muted">Nikogo jeszcze nie ma w obsadzie. Ile osób na jakim stanowisku — w „Info”.</p>}
      {shownDays.map((d) => (
        <div key={d.day} className="crew-positions-day">
          {multiDay && <h4 className="crew-positions-dayname">{formatDayText(d.day)}</h4>}
          <ul className="crew-places" aria-label={multiDay ? `Potrzebni w obsadzie — ${dayShort(d.day)}` : 'Potrzebni w obsadzie'} data-feature-id="crew.demands">
            {d.groups.flatMap((g) => [...g.places.map((place) => placeRow(d.day, g, { place, extra: false })), ...(g.extra ?? []).map((place) => placeRow(d.day, g, { place, extra: true }))])}
          </ul>
        </div>
      ))}

      {lastDay && (
        <div className="dialog-backdrop">
          <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="crew-last-day-title">
            <DialogLayer onClose={() => setLastDay(null)} busy={releasePerson.isPending} />
            <h2 id="crew-last-day-title">Zdjąć osobę z ostatniego dnia?</h2>
            <p>
              {lastDay.personName} nie pracuje w innych dniach tego wydarzenia — po zdjęciu z miejsca ({dayShort(lastDay.day)}) zniknie z obsady. Miejsce zostanie wolne.
            </p>
            <div className="actions">
              <button
                className="btn primary"
                type="button"
                disabled={releasePerson.isPending}
                onClick={() => {
                  releasePerson.mutate({ assignmentId: lastDay.assignmentId, day: lastDay.day });
                  setLastDay(null);
                }}
              >
                Zdejmij i usuń z obsady
              </button>
              <button className="btn" type="button" onClick={() => setLastDay(null)}>
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
      {askDays && (
        <div className="dialog-backdrop">
          <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="crew-other-days-title">
            <DialogLayer onClose={() => setAskDays(null)} busy={fillOthers.isPending} />
            <h2 id="crew-other-days-title">Obsadzić tę osobę także w pozostałych dniach?</h2>
            <p>
              {askDays.personName} — {askDays.days.map(dayShort).join(', ')}. Dni, w których osoba jest niedostępna, zostaną pominięte; zajętość w innym wydarzeniu pokaże się jako kolizja.
            </p>
            {fillOthers.error && (
              <p className="alert" role="alert">
                {errorText(fillOthers.error, 'Nie udało się obsadzić')}
              </p>
            )}
            <div className="actions">
              <button className="btn primary" type="button" disabled={fillOthers.isPending} onClick={() => fillOthers.mutate({ positionId: askDays.positionId })}>
                Tak, wszystkie dni
              </button>
              <button
                className="btn"
                type="button"
                onClick={() => {
                  setAskDays(null);
                  onChanged?.();
                }}
              >
                Nie, tylko ten dzień
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * The options of a person picker (production 2026-10-26): someone „Niedostępny” on a day it covers is greyed out and
 * cannot be picked — „Karol Zbaracki — niedostępny 26.10”.
 */
export function personOptions(list: readonly Person[], unavailable: UnavailableDays, days: readonly string[]) {
  return list.map((p) => {
    const hit = blockedDays(unavailable, p.id, days);
    return (
      <option key={p.id} value={p.id} disabled={hit.length > 0}>
        {hit.length > 0 ? unavailableLabel(p.name ?? 'Osoba', hit) : (p.name ?? 'Osoba')}
      </option>
    );
  });
}
